import * as d3 from 'd3-selection'
import * as topojson from 'topojson-client'
import Draggable from './draggable/'
import { drag as _drag } from 'd3-drag'
import {interpolate} from 'd3-interpolate'
import ProjectionController from './projections/controller'
import bubbleMapAnimation from './animations/bubble-map-basic-animation'
import filledMapAnimation from './animations/filled-map-basic-animation'
import spikeMapAnimation from './animations/spike-map-basic-animation'
import {legend as spikeLegend} from './animations/spike-map-basic-animation'
import {legend as bubbleLegend} from './animations/bubble-map-basic-animation'
import back from 'back-container'

export const animations = [
  bubbleMapAnimation,
  filledMapAnimation,
  spikeMapAnimation
]

export const legend = function (chart, type, {width, height}, opts) {
  if (type === 'spike-map') {
    return spikeLegend(chart, {width, height})
  } else if (type === 'bubble-map') {
    return bubbleLegend(chart, {width, height}, opts)
  } else {
    return null
  }
}

const isDeemphasizeChange = (container, config) => {
  let deemphasize0 = container.classed('deemphasize')
    , deemphasize1 = !!config.deemphasize

  return deemphasize0 !== deemphasize1
}

export const preAnimate = function (chart, selection, series, scalers, duration = 0) {
  // TODO This will break if the chart changes to a new type other than map
  let svg = d3.select(selection.node().ownerSVGElement)
              .classed('has-map-serie', true)
    , config = chart.datum().axes.map
    , node = config.node
    , prevMap = selection.selectAll('g.map')
    , map = selection.selectAll('g.map')
                      .data(() => {
                        if (prevMap.size() && prevMap.datum().id == node.key) {
                          // Loading the same node, potentially w/ a different
                          // projection, so return the previous data
                          // The projection is attached to this data. That's fine
                          // The transformer will adjust if necessary
                          let prev = prevMap.data()
                          prev[0].globe = config.globe
                          return prev
                        }
                        let geojson = topojson.feature(config.topojson, config.topojson.objects.view)
                        return [{
                          id: node.key,
                          topo: config.topojson,
                          geojson,
                          globe: config.globe,
                          node
                       }]
                      }, d => d.id)

  let exit = map.exit()
    , enter = map.enter()
                  .insert('g', ':first-child') // New layers come in under the old layer
                    .attr('class', `map`)
    , _map = map.merge(enter)
    , deemphasizeChanged = isDeemphasizeChange(_map, config)
    , draggable = new Draggable(config, chart.ee, svg, _map)
    , controller = new ProjectionController

  let cancelRotation = () => {
    _map.selectAll('path').transition()
    chart.node().__autoRotater.stop()
  }

  let clearHoverBorder = () => {
    _map.selectAll('.hover-border').remove()
  }

  chart.ee.on('globe:rotate', cancelRotation)

  if (chart.node().__autoRotater && !deemphasizeChanged) {
    cancelRotation()
  }

  svg.call(_drag()
       .on('start.map', draggable.start)
       .on('drag.map', draggable.drag)
       .on('end.map', draggable.end)
       .filter(e => {
         // Make sure drag events aren't called on back-button-container elements.
         // Remove the #Document elements in the path.
         return !e.composedPath().slice(0, -2).find(node => {
           return d3.select(node)
                    .classed('back-button-container')
         })
       }))

  selection.call(back, config.back)

  // Add the path elements
  _map.selectAll('path')
      .data(d => {
        if (d.geojson.features) {
          return d.geojson.features
        } else if (d.geojson.type === 'Feature') {
          // Assume it's a single feature
          return [d.geojson]
        } else {
          return []
        }
      }, d => d.id)
      .enter()
        .append('path')
        .attr('class', 'feature')
        .merge(_map.selectAll('path'))
        .classed('no-drilldown', !node.children?.length)

  let transition = _map.transition() // Main transition on the entering nodes
    , transformer = controller.getTransformer(_map, exit, {
      duration,
      container: _map,
      globe: config.globe,
      ee: chart.ee,
      chart: chart,
      width: chart.datum().dimensions.width,
      height: chart.datum().dimensions.height,
      zoomTo: config.zoomTo,
      deemphasizeChanged,
    })

  transition.duration(!!duration ? transformer.getDuration() : 0)

  // This old low-res feature will be re-projected if projection types changed
  exit.selectAll('path.feature')
      .filter(d => d.id == config.topojson.id)
      .raise() // Make sure it's on top of the old features within the plot

  // Fade in new nodes
  transition.tween('fade-in-and-project', () => {
              // Fade in the new feature
              let interpolateOpacity = interpolate(1e-6, 1)

              return t => {
                enter.style('opacity', interpolateOpacity(t))
                transformer.transform(t) // Invoke the projection transformer
              }
            })

  // Store `deemphasize` state so we can compare future updates
  _map.classed('deemphasize', config.deemphasize)

  transition.end()
            .then(() => {
              // By the time the new feature has faded in and has full opacity
              // we can remove the exiting features
              exit.remove()

              // Bind proper event handlers in case a transition modified original
              // paths
              _map.selectAll('path.feature')
                  .on('mouseenter.raiser', function () { // namespace event to prevent conflicts
                    // Clear previous hover styling
                    clearHoverBorder()

                    // Add a new feature at the end which will have styling
                    // for hover. We used to raise the feature directly, but
                    // it causes issues with its siblings and weird topojson
                    d3.select(this.parentNode)
                      .append(() => this.cloneNode(true))
                      .classed('hover-border', true)
                      .style('fill', 'none') // Clear any previous fills
                  })
                  .on('mouseout.raiser', clearHoverBorder)
                  .on('click', (e, d) => {
                    chart.ee.emit('chart-event', 'map:zoomTo', [d])
                  })

              transformer.end()
            })
            .catch(e => {
              // Uninterrupted, ignore
            })

  // New delayed transition for fade out on exit
  exit.transition()
      .duration(transition.duration())
      .delay(transition.duration() * .2) // Delay the fade-out of the previous map
      .tween('fade-out', () => {
        let interpolateOpacity = interpolate(1, 1e-6)

        return t => {
          exit.style('opacity', interpolateOpacity(t))
        }
      })
}
